import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import { isToday, formatTime, formatDate } from "../../utils/dateUtils";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  ListItem,
  Box,
  Typography,
  ButtonGroup,
  Button,
  Tooltip,
} from "@material-ui/core";
import { resolveContactAvatar } from "../../utils/avatar";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { isAdminProfile } from "../../helpers/userPermissions";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import CustomChip from "../ui/CustomChip";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { Check as Done, XCircle as HighlightOff, ArrowLeftRight as SwapHoriz } from "lucide-react";
import {
  MessageSquare as LucideMessageSquare,
  Contact as LucideContact,
  VolumeX as LucideVolumeOff,
  Star,
} from "lucide-react";
import useMutedGroups from "../../hooks/useMutedGroups";
import useCrossConnectionAcceptGuard from "../../hooks/useCrossConnectionAcceptGuard";
import {
  parseMediaString,
  getMediaTypeInfo,
} from "../../helpers/mediaTypeHelper";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    height: "60px",
    padding: "8px 12px",
    borderRadius: `${theme.borderRadius.medium} !important`,
    border: `1px solid ${theme.palette.divider} !important`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}15 !important`,
    },
    "&.Mui-selected": {
      backgroundColor: `${theme.palette.primary.main}15 !important`,
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}15 !important`,
      },
    },
    ".MuiPopover-root &": {
      padding: theme.spacing(0.5, 0.75),
      borderRadius: theme.shape.borderRadius,
      height: "55px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "6px 8px",
      ".MuiPopover-root &": {
        padding: theme.spacing(0.5, 0.5),
      },
    },
  },

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginLeft: "8px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "6px",
    },
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  contactName: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
    fontSize: "0.9rem !important",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "70%",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
      maxWidth: "60%",
    },
  },

  lastMessageTime: {
    fontSize: "0.9rem !important",
    color: theme.palette.text.secondary,
    marginLeft: "auto",
    minWidth: "40px",
    textAlign: "right",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem !important",
      minWidth: "35px",
    },
  },

  lastMessageTimeUnread: {
    fontSize: "0.9rem !important",
    color: theme.palette.success.main,
    fontWeight: "bold",
    marginLeft: "auto",
    minWidth: "40px",
    textAlign: "right",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem !important",
      minWidth: "35px",
    },
  },

  // 🔇 Ícone de grupo silenciado
  mutedIcon: {
    color: theme.palette.text.disabled,
    marginLeft: "4px",
    opacity: 0.7,
    flexShrink: 0,
  },

  // Container para timestamp e ícone de silenciado
  timestampContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
    gap: "4px",
  },

  connectionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "4px",
    width: "100%",
  },

  ticketInfo: {
    flex: 1,
    minWidth: 0,
    marginLeft: "8px",
  },

  ticketHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  connectionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginTop: 4,
  },

  connectionName: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },

  notificationItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5, 1),
    },
  },

  button: {
    minWidth: "18px",
    height: "18px",
    color: theme.palette.action.active,
    "&:hover": {
      backgroundColor: "rgba(189, 189, 189, 0.5) !important",
    },
  },

  // Botão de aceitar (verde no hover)
  buttonAccept: {
    color: theme.palette.action.active,
    backgroundColor: "transparent !important",
    "&:hover": {
      "& svg": {
        color: "#4caf50 !important",
      },
    },
  },

  // Botão de transferir (laranja no hover)
  buttonTransfer: {
    color: theme.palette.action.active,
    backgroundColor: "transparent !important",
    "&:hover": {
      "& svg": {
        color: "#ff9800 !important",
      },
    },
  },

  // Botão de fechar (vermelho no hover)
  buttonClose: {
    color: theme.palette.action.active,
    backgroundColor: "transparent !important",
    "&:hover": {
      "& svg": {
        color: "#f44336 !important",
      },
    },
  },

  iconSize: {
    width: 16,
    height: 16,
    color: theme.palette.action.active,
  },

  buttonGroup: {
    display: "flex",
    alignItems: "center",
    "& .MuiButtonGroup-grouped": {
      border: "none",
      minWidth: "unset",
    },
    "& .MuiButton-root": {
      minWidth: "unset",
      padding: "4px",
      border: "none",
      backgroundColor: "transparent !important",
    },
  },

  buttonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "3px",
  },
}));

const TicketListItem = ({
  ticket,
  setTabOpen,
  setModalOpen,
  hideLastMessage = false,
}) => {
  const classes = useStyles();
  const toast = useToast();
  const { guardAccept, acceptGuardModal, checking } = useCrossConnectionAcceptGuard();

  const history = useHistory();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { get: getSetting } = useCompanySettings();
  
  // 🔇 Hook para verificar grupos silenciados
  const { isGroupMuted } = useMutedGroups();
  const isMuted = ticket.isGroup && isGroupMuted(ticket.id);
  const [, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSelectTicket = useCallback(
    (ticket) => {
      if (!isMounted.current) return;

      const code = uuidv4();
      const { id, uuid } = ticket;
      setCurrentTicket({ id, uuid, code });

      setTimeout(() => {
        if (isMounted.current) {
          history.push(`/tickets/${ticket.uuid}`);
        }
      }, 10);
    },
    [history, setCurrentTicket]
  );

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      if (!isMounted.current) return;

      if (setModalOpen) setModalOpen(true);

      setAcceptTicketWithouSelectQueueOpen(true);
    },
    [setModalOpen]
  );

  const handleOpenTransferModal = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (!isMounted.current) return;
    setLoading(true);

    if (setModalOpen) setModalOpen(true);

    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
  };

  const handleCloseAlert = useCallback(() => {
    if (isMounted.current) {
      setOpenAlert(false);
      setLoading(false);
    }
  }, [setLoading]);

  const handleCloseIgnoreTicket = async (id) => {
    if (!isMounted.current) return;
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
    if (isMounted.current) {
      setLoading(false);
    }
  };

  const handleSendMessage = async (id) => {
    if (!isMounted.current) return;
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      if (isMounted.current) {
        toastError(err);
      }
    }

    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      if (isMounted.current) {
        toastError(err);
      }
    }
  };

  const handleAcepptTicket = async (id) => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const desiredStatus =
        ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open";
      const otherTicket = await api.put(`/tickets/${id}`, {
        status: desiredStatus,
        userId: user?.id,
      });

      if (!isMounted.current) return;

      // ✅ Helper para navegação após aceitar ticket
      // ORDEM CORRETA: 1. Tab → 2. Contexto → 3. Navegação automática via useEffect
      const navigateToAcceptedTicket = (ticketData, targetStatus, showRedirectToast = false) => {
        // ✅ FIX: Mostrar toast de redirecionamento se aplicável
        if (showRedirectToast && ticketData.redirectedToExisting) {
          toast.info(ticketData.redirectMessage || "Já existe um atendimento aberto para este contato. Você foi redirecionado.");
        }
        
        // 1. Mudar aba PRIMEIRO - garante que lista está na aba correta
        if (setTabOpen) setTabOpen(targetStatus);
        
        // 2. setCurrentTicket dispara history.push via useEffect no TicketsContext
        setCurrentTicket({
          id: ticketData.id,
          uuid: ticketData.uuid,
          code: "#" + targetStatus,
        });
      };

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user?.name || "Outro usuário");
          setQueueTicketOpen(otherTicket.data.queue?.name || "");
        } else {
          setLoading(false);
          // ✅ FIX: Mostrar toast de redirecionamento quando navegar para o ticket
          navigateToAcceptedTicket(otherTicket.data, otherTicket.data.status || desiredStatus, true);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });

          if (!isMounted.current) return;

          if (
            setting.sendGreetingAccepted === "enabled" &&
            (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
          ) {
            await handleSendMessage(ticket.id);
          }
        } catch (err) {
          if (isMounted.current) {
            toastError(err);
          }
        }

        if (isMounted.current) {
          setLoading(false);
        }
        
        navigateToAcceptedTicket(ticket, desiredStatus);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  const truncate = (str, len) => {
    if (str && str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const getConnectionProps = useMemo(() => {
    // ✅ GRUPOS: Não exibir nome da conexão específica (SUPORTE, LOJA, etc.)
    // Grupos podem estar em múltiplas conexões, exibir apenas o tipo de canal
    const isGroup = ticket.isGroup;
    
    if (ticket.channel === "whatsapp") {
      return {
        label: isGroup ? "WhatsApp" : truncate(ticket.whatsapp?.name || "WhatsApp", 20),
        backgroundColor: "rgba(37, 211, 102, 0.1)",
        textColor: "#25D366",
        borderColor: "#25D366",
      };
    }
    if (ticket.channel === "instagram") {
      return {
        label: isGroup ? "Instagram" : truncate(ticket.whatsapp?.name || "Instagram", 20),
        backgroundColor: "rgba(225, 48, 108, 0.1)",
        textColor: "#e1306c",
        borderColor: "#e1306c",
      };
    }
    if (ticket.channel === "facebook") {
      return {
        label: isGroup ? "Facebook" : truncate(ticket.whatsapp?.name || "Facebook", 20),
        backgroundColor: "rgba(59, 89, 152, 0.1)",
        textColor: "#3b5998",
        borderColor: "#3b5998",
      };
    }
    return {
      label: isGroup 
        ? (ticket.channel?.toUpperCase() || "Conexão")
        : truncate(ticket.whatsapp?.name || ticket.channel?.toUpperCase() || "Conexão", 20),
      backgroundColor: "rgba(125, 125, 125, 0.1)",
      textColor: "#7D7D7D",
      borderColor: "#7D7D7D",
    };
  }, [ticket.channel, ticket.whatsapp, ticket.isGroup]);

  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const renderLastMessage = (msg) => {
    if (!msg) return "";

    if (msg === "Contato" || msg.startsWith("Contato:")) {
      const contactName =
        msg === "Contato" ? "" : msg.replace("Contato:", "").trim();
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <LucideContact size={14} />
          <span>Contato{contactName ? `: ${contactName}` : ""}</span>
        </span>
      );
    }

    const mediaInfo = parseMediaString(msg);
    if (mediaInfo) {
      const { icon: Icon, label, caption } = mediaInfo;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon size={14} />
          <span>
            {label}
            {caption ? `: ${caption}` : ""}
          </span>
        </span>
      );
    }

    if (msg.startsWith("media:")) {
      const payload = msg.slice(6);
      const [type, ...rest] = payload.split("|");
      const caption = rest.join("|") || "";

      const typeInfo = getMediaTypeInfo(type);
      const { icon: Icon, label } = typeInfo;

      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon size={14} />
          <span>
            {label}
            {caption &&
            caption.trim() &&
            !caption.match(
              /^\d+\.(mp3|ogg|wav|m4a|opus|webm|jpg|png|mp4|pdf|doc|docx)$/i
            )
              ? `: ${caption}`
              : ""}
          </span>
        </span>
      );
    }

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <LucideMessageSquare size={14} />
        {msg}
      </span>
    );
  };

  const getLastMessagePlainText = (msg) => {
    if (!msg) return "";
    if (msg === "Contato" || msg.startsWith("Contato:")) {
      return "Contato";
    }
    const mediaInfo = parseMediaString(msg);
    if (mediaInfo) {
      const { label, caption } = mediaInfo;
      return `${label}${caption ? `: ${caption}` : ""}`;
    }
    if (msg.startsWith("media:")) {
      const payload = msg.slice(6);
      const [type, ...rest] = payload.split("|");
      const caption = rest.join("|") || "";
      const { label } = getMediaTypeInfo(type);
      if (
        caption &&
        caption.trim() &&
        !caption.match(
          /^\d+\.(mp3|ogg|wav|m4a|opus|webm|jpg|png|mp4|pdf|doc|docx)$/i
        )
      ) {
        return `${label}: ${caption}`;
      }
      return label;
    }
    return msg;
  };

  return (
    <>
      {acceptGuardModal}
      {transferTicketModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1500,
            backgroundColor: "transparent",
          }}
          onClick={stopPropagation}
        />
      )}
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            if (isMounted.current) {
              if (setModalOpen) setModalOpen(false);
              setAcceptTicketWithouSelectQueueOpen(false);
            }
          }}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            if (isMounted.current) {
              if (setModalOpen) setModalOpen(false);
              setTransferTicketModalOpen(false);
            }
          }}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}

      <ListItem
        button
        dense
        onClick={() => handleSelectTicket(ticket)}
        className={classes.ticket}
        style={{ height: "60px" }}
      >
        <Avatar
          style={{
            width: 32,
            height: 32,
          }}
          src={resolveContactAvatar(ticket?.contact)}
          alt={ticket?.contact?.name}
        />

        <Box className={classes.ticketInfo}>
          <Box className={classes.ticketHeader}>
            <Typography className={classes.contactName} component="span">
              {ticket.isStarred && (
                <Star 
                  size={12} 
                  fill="#f59e0b" 
                  color="#f59e0b" 
                  style={{ marginRight: 4, verticalAlign: 'middle' }}
                />
              )}
              {truncate(ticket.contact?.name, 22)}
            </Typography>

            <Box className={classes.timestampContainer}>
              {/* 🔇 Ícone de grupo silenciado */}
              {isMuted && (
                <Tooltip title="Grupo silenciado">
                  <span style={{ display: "flex", alignItems: "center" }}>
                    <LucideVolumeOff size={12} className={classes.mutedIcon} />
                  </span>
                </Tooltip>
              )}
              <Typography
                className={
                  Number(ticket.unreadMessages) > 0
                    ? classes.lastMessageTimeUnread
                    : classes.lastMessageTime
                }
                component="span"
              >
                {isToday(ticket.updatedAt)
                  ? formatTime(ticket.updatedAt)
                  : formatDate(ticket.updatedAt)}
              </Typography>
            </Box>
          </Box>

          <Box className={classes.connectionContainer}>
            {!hideLastMessage && (
              <Tooltip
                title={getLastMessagePlainText(ticket.lastMessage) || ""}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  noWrap
                  component="span"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {ticket.lastMessage
                    ? renderLastMessage(truncate(ticket.lastMessage, 15))
                    : ""}
                </Typography>
              </Tooltip>
            )}
            <CustomChip
              label={getConnectionProps.label}
              size="small"
              icon={
                <ConnectionIcon
                  connectionType={ticket.channel}
                  style={{ fontSize: "0.65rem" }}
                />
              }
              backgroundColor={getConnectionProps.backgroundColor}
              textColor={getConnectionProps.textColor}
              borderColor={getConnectionProps.borderColor}
              withTooltip
              tooltipTitle={ticket.isGroup ? getConnectionProps.label : (ticket.whatsapp?.name || "Conexão")}
              customStyles={{
                height: "16px",
                padding: "1px 4px",
                fontSize: "0.65rem",
              }}
            />

            {ticket.status === "pending" && (
              <Box className={classes.buttonsContainer}>
                <ButtonGroup className={classes.buttonGroup} variant="text">

                  {ticket.status === "pending" &&
                    (ticket.queueId === null ||
                      ticket.queueId === undefined) && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.accept")}`}
                      >
                        <Button
                          className={classes.buttonAccept}
                          aria-label="accept"
                          disabled={checking}
                          onClick={(e) => {
                            e.stopPropagation();
                            guardAccept(ticket, () => handleOpenAcceptTicketWithouSelectQueue());
                          }}
                        >
                          <Done className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}
                  {ticket.status === "pending" && ticket.queueId != null && (
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Button
                        className={classes.buttonAccept}
                        aria-label="accept"
                        disabled={checking}
                        onClick={(e) => guardAccept(ticket, () => handleAcepptTicket(ticket.id))}
                      >
                        <Done className={classes.iconSize} />
                      </Button>
                    </Tooltip>
                  )}

                  {!ticket.isGroup &&
                    (ticket.status === "pending" ||
                      ticket.status === "open") && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.transfer")}`}
                      >
                        <Button
                          className={classes.buttonTransfer}
                          aria-label="transfer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenTransferModal(e);
                          }}
                        >
                          <SwapHoriz className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}

                  {ticket.status === "pending" &&
                    (user.userClosePendingTicket === "enabled" ||
                      isAdminProfile(user)) && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.ignore")}`}
                      >
                        <Button
                          className={classes.buttonClose}
                          aria-label="ignore"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseIgnoreTicket(ticket.id);
                          }}
                        >
                          <HighlightOff className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}
                </ButtonGroup>
              </Box>
            )}
          </Box>
        </Box>
      </ListItem>
    </>
  );
};

export default React.memo(TicketListItem, (prevProps, nextProps) => {
  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.status === nextProps.ticket.status &&
    prevProps.ticket.unreadMessages === nextProps.ticket.unreadMessages &&
    prevProps.ticket.lastMessage === nextProps.ticket.lastMessage &&
    prevProps.ticket.updatedAt === nextProps.ticket.updatedAt &&
    prevProps.hideLastMessage === nextProps.hideLastMessage
  );
});
